import type { ExportRequest } from '../api/types'

/** The column headers an export will actually carry.
 *
 *  This mirrors `wide_header` in `backend/app/services/export.py`, and the
 *  two have to agree: the panel shows this list as what the file contains,
 *  so a divergence is the panel lying about a file the user has not opened
 *  yet. Three things are load-bearing, and each was a way to get it wrong:
 *
 *  - **Order is the backend's**, `station, axis_group, code` — not the
 *    alphabetical-by-display-name order the parameter checkboxes use. A
 *    preview listing the same columns in a different order reads as the
 *    file's layout and is not.
 *  - **The station prefix appears only for more than one station**, because
 *    that is when it stops being noise and starts being the only thing
 *    telling two `Discharge (m³/s)` apart.
 *  - **A `-` unit is still printed**, as `Signal Quality (CSQ) (-)`. The
 *    parameter list hides that unit as meaningless; the file does not, and
 *    this describes the file.
 */
export interface ExportColumn {
  station: string
  code: string
  display_name: string
  unit: string
  axis_group: string
}

export interface HeaderOptions {
  includeQuality: boolean
}

export function exportHeaders(columns: ExportColumn[], timezone: string,
                              { includeQuality }: HeaderOptions,
): string[] {
  const prefix = new Set(columns.map((c) => c.station)).size > 1
  const ordered = [...columns].sort((a, b) =>
    a.station.localeCompare(b.station)
    || a.axis_group.localeCompare(b.axis_group)
    || a.code.localeCompare(b.code))

  const head = [`timestamp (${timezone})`]
  for (const column of ordered) {
    const name = prefix ? `${column.station} ${column.display_name}`
      : column.display_name
    head.push(column.unit ? `${name} (${column.unit})` : name)
    // Beside its value, not collected at the end: the backend writes them
    // interleaved so a reader never has to count columns to pair them up.
    if (includeQuality) head.push(`${name} quality`)
  }
  return head
}

/** The POST body for a download: the window as instants, the format and
 *  whether to carry quality flags — and nothing else. Resolution, aggregate
 *  and layout are retired and the API refuses each by name, so this is the
 *  one place that could send them and does not. */
export function exportRequest(options: {
  stations: string[]
  parameters: string[]
  from: Date
  to: Date
  format: 'csv' | 'xlsx' | 'csv-excel'
  includeQuality: boolean
  /** A project-named file, asked for when the export is made from inside a
   *  project (`lib/projectForm` projectExportName); the server sanitises it
   *  (`export.safe_filename`) and names the file itself when it is absent. */
  filename?: string
}): ExportRequest {
  return {
    stations: options.stations,
    parameters: options.parameters,
    from: options.from.toISOString(),
    to: options.to.toISOString(),
    format: options.format,
    include_quality: options.includeQuality,
    ...(options.filename ? { filename: options.filename } : {}),
  }
}
